import React, { useState } from "react";
import StaffingPreferenceSwitch from "./StaffingPreferenceSwitch";
import Flex from "../Generic/Flex";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../Generic/AppCard";
import { AnimatePresence, motion } from "framer-motion";
import StaffingPreferenceSelect from "./StaffingPreferenceSelect";
import { CaregiverStaffingPreferenceTypes, StaffingPreference } from "../../messages";
import StaffingPreferenceMultiSelect from "./StaffingPreferencesMultiSelect";
import "./MystaffingPreferences.scss";
import { CaretDownBoldIcon, CaretUpBoldIcon } from "../../utils/icones";
import StaffingPreferenceText from "./StaffingPreferencesText";

interface StaffingPreferenceSectionProps {
  title: string;
  iconSrc: JSX.Element;
  preferences: StaffingPreference[];
  onChangeAnswer: (
    type: CaregiverStaffingPreferenceTypes,
    value: boolean | string | number | string[] | null
  ) => void;
}

const StaffingPreferenceSection = (props: StaffingPreferenceSectionProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const studyingTimesPreference =
    props.preferences.find((p) => p.type === "studying_times") ?? null;

  const studyingTimesQuestionProps =
    studyingTimesPreference !== null && studyingTimesPreference.type === "studying_times"
      ? {
          label: studyingTimesPreference.label,
          type: studyingTimesPreference.type,
          answer: studyingTimesPreference.answer,
          onChange: (type: CaregiverStaffingPreferenceTypes, value: string | null) =>
            props.onChangeAnswer(type, value),
        }
      : null;

  return (
    <AppCard>
      <AppCardHeader
        borderless={true}
        onClick={() => setIsOpen(!isOpen)}
        className="MyStaffingPreferences__SectionHeader"
      >
        <Flex gap={10} alignItems="center">
          <div className="MyStaffingPreferences__SectionIcon">{props.iconSrc}</div>
          <AppCardTitle className="MyStaffingPreferences__SectionTitle">{props.title}</AppCardTitle>
        </Flex>
        {isOpen ? <CaretDownBoldIcon /> : <CaretUpBoldIcon />}
      </AppCardHeader>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <AppCardContent className="MyStaffingPreferences__SectionContent">
              {props.preferences.map((preference) => {
                if (preference.type === "studying") {
                  return (
                    <StudyingStafingPreferenceQuestion
                      key={preference.type}
                      type={preference.type}
                      label={preference.label}
                      answer={preference.answer}
                      onChange={(type: CaregiverStaffingPreferenceTypes, value: boolean) =>
                        props.onChangeAnswer(type, value)
                      }
                      studyingTimesQuestion={studyingTimesQuestionProps}
                    />
                  );
                }

                return (
                  <StaffingPreferenceBody
                    key={preference.type}
                    {...preference}
                    onChange={props.onChangeAnswer}
                  />
                );
              })}
            </AppCardContent>
          </motion.section>
        )}
      </AnimatePresence>
    </AppCard>
  );
};

export default StaffingPreferenceSection;

const StaffingPreferenceBody = (
  props: StaffingPreference & {
    onChange: (
      type: CaregiverStaffingPreferenceTypes,
      value: boolean | string | number | string[] | null
    ) => void;
  }
) => {
  switch (props.questionType) {
    case "Select":
      return (
        <StaffingPreferenceSelect
          label={props.label}
          type={props.type}
          options={props.options}
          value={props.answer}
          onChange={props.onChange}
        />
      );
    case "Switch":
      return (
        <StaffingPreferenceSwitch
          label={props.label}
          type={props.type}
          value={props.answer}
          onChange={props.onChange}
        />
      );
    case "MultiSelect":
      return (
        <StaffingPreferenceMultiSelect
          label={props.label}
          type={props.type}
          options={props.options}
          values={props.answer}
          onChange={props.onChange}
        />
      );
    case "Text":
      if (props.type === "studying_times") {
        return null;
      }

      return (
        <StaffingPreferenceText
          label={props.label}
          type={props.type}
          value={props.answer}
          onChange={props.onChange}
        />
      );
    default: {
      console.log(`Error: Invalid question type `);
      return <div> Error </div>;
    }
  }
};

const StudyingStafingPreferenceQuestion = (props: {
  label: string;
  type: CaregiverStaffingPreferenceTypes;
  answer: boolean;
  studyingTimesQuestion: {
    type: CaregiverStaffingPreferenceTypes;
    label: string;
    answer: string | null;
    onChange: (type: CaregiverStaffingPreferenceTypes, value: string | null) => void;
  } | null;
  onChange: (type: CaregiverStaffingPreferenceTypes, value: boolean) => void;
}) => {
  const [isStudying, setIsStudying] = useState(props.answer);

  const handleChangeIsStudying = (type: CaregiverStaffingPreferenceTypes, value: boolean) => {
    setIsStudying(value);
    props.onChange(type, value);
  };

  return props.studyingTimesQuestion === null ? null : (
    <>
      <StaffingPreferenceSwitch
        label={props.label}
        type={"studying"}
        value={props.answer}
        onChange={handleChangeIsStudying}
      />
      <AnimatePresence initial={false}>
        {isStudying && (
          <motion.section
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <StaffingPreferenceText
              label={props.studyingTimesQuestion.label}
              value={props.studyingTimesQuestion.answer}
              type={props.studyingTimesQuestion.type}
              onChange={props.studyingTimesQuestion.onChange}
            />
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};
